import React from 'react';
import { AlertCircle, Clock, RotateCcw } from 'lucide-react';

interface ToggleVencidosProps {
  /** True si la pestaña está mostrando pedidos vencidos. */
  modoVencidos: boolean;
  /** Callback al alternar entre vigentes y vencidos. */
  onCambio: (modoVencidos: boolean) => void;
  /**
   * Cuenta opcional de vencidos para mostrar en el botón "Ver
   * vencidos" cuando estamos en modo vigentes. Si se omite, no se
   * muestra el badge numérico.
   */
  contadorVencidos?: number;
  /** Texto opcional para personalizar el botón. */
  labelVer?: string;
  labelVolver?: string;
}

/**
 * Toggle reusable para alternar entre la vista "vigentes" (default)
 * y "vencidos" (fecha_entrega < hoy) en las pestañas operativas de
 * /pedidos. Mismo patrón visual que el banner de modo histórico de
 * Minutas — banner ámbar mientras el modo vencidos está activo.
 */
const ToggleVencidos: React.FC<ToggleVencidosProps> = ({
  modoVencidos,
  onCambio,
  contadorVencidos,
  labelVer = 'Ver vencidos',
  labelVolver = 'Volver a vigentes',
}) => {
  if (modoVencidos) {
    return (
      <div className="mb-4 px-4 py-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-sm flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Mostrando pedidos vencidos (fecha de entrega ya pasó).
        </span>
        <button
          type="button"
          onClick={() => onCambio(false)}
          className="px-3 py-1 text-xs bg-white dark:bg-gray-800 text-amber-700 dark:text-amber-300 rounded-md hover:bg-amber-100 dark:hover:bg-gray-700 border border-amber-300 dark:border-amber-700 flex items-center gap-1"
        >
          <RotateCcw className="w-3 h-3" />
          {labelVolver}
        </button>
      </div>
    );
  }

  // Modo vigentes — botón discreto que invita a ver los vencidos.
  return (
    <div className="mb-4 flex justify-end">
      <button
        type="button"
        onClick={() => onCambio(true)}
        className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-md border border-gray-300 dark:border-gray-600 hover:border-amber-300 dark:hover:border-amber-700 flex items-center gap-1.5 transition-colors"
        title="Pedidos con fecha de entrega ya pasada"
      >
        <Clock className="w-3.5 h-3.5" />
        {labelVer}
        {typeof contadorVencidos === 'number' && contadorVencidos > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 rounded-full font-medium">
            {contadorVencidos}
          </span>
        )}
      </button>
    </div>
  );
};

export default ToggleVencidos;
